import { FC, useContext, useState, useCallback } from "react";

import { FormType, getLocalesFromIsoLocale, PeerToPeerQueryParams, RouteBuilders } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { getIsDonateMode } from "../../../blocks/peer-to-peer/CampaignActions/helpers/isDonateMode";
import { FundDesignation } from "../../../blocks/selection/donation-selection/FundDesignation";
import { GiftAidBlock } from "../../../blocks/selection/donation-selection/GiftAidBlock";
import { BurstProvider } from "../../../contexts/burstContext";
import { DonationPaymentInput, useFormV2Context } from "../../../contexts/FormV2Context";
import { HeartsAnimationProvider } from "../../../contexts/heartsAnimationContext";
import { DonationFormCheckoutFooter } from "../shared/CheckoutFooter/DonationFormCheckoutFooter";

import { MobileCampaignBanner } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignBanner/MobileCampaignBanner";
import { DonorListItem } from "@/canvas/features/FormV2/blocks/donor-list/DonorListItem";
import { DonorsListBlock } from "@/canvas/features/FormV2/blocks/donor-list/DonorsListBlock";
import { DonorsListDialog } from "@/canvas/features/FormV2/blocks/donor-list/DonorsListDialog";
import { FundraisersInformationDialog } from "@/canvas/features/FormV2/blocks/donor-list/FundraisersInformationDialog";
import { FundraisersListBlock } from "@/canvas/features/FormV2/blocks/donor-list/FundraisersListBlock";
import { useDonorsListBlockState } from "@/canvas/features/FormV2/blocks/donor-list/useDonorsListBlockState";
import {
  useDonorsListDialogServerState,
  useDonorsListDialogState,
} from "@/canvas/features/FormV2/blocks/donor-list/useDonorsListDialogState";
import { GenerateReceiptInfobox } from "@/canvas/features/FormV2/blocks/payment/GenerateReceiptInfobox";
import { ZeffyBusinessModelInfobox } from "@/canvas/features/FormV2/blocks/payment/ZeffyBusinessModelInfobox";
import { FundraiseDialog } from "@/canvas/features/FormV2/blocks/peer-to-peer/CampaignActions/FundraiseDialog/FundraiseDialog";
import { PeerToPeerPrimaryCampaignActions } from "@/canvas/features/FormV2/blocks/peer-to-peer/CampaignActions/PeerToPeerPrimaryCampaignActions";
import { PeerToPeerPrimaryCampaignSummary } from "@/canvas/features/FormV2/blocks/peer-to-peer/CampaignActions/PeerToPeerPrimaryCampaignSummary";
import { useOpenPeerToPeerRegistrationForm } from "@/canvas/features/FormV2/blocks/peer-to-peer/CampaignActions/useOpenPeerToPeerRegistrationForm";
import { IndividualFundraiserCard } from "@/canvas/features/FormV2/blocks/peer-to-peer/IndividualFundraiserCard";
import { PrimaryCampaignTypeChip } from "@/canvas/features/FormV2/blocks/peer-to-peer/PrimaryCampaignTypeChip";
import { TeamFundraiserCard } from "@/canvas/features/FormV2/blocks/peer-to-peer/TeamFundraiserCard";
import { Amounts } from "@/canvas/features/FormV2/blocks/selection/donation-selection/DonationForm/Amounts";
import { useDonationFirstStepSubmit } from "@/canvas/features/FormV2/blocks/selection/donation-selection/DonationForm/useDonationFirststepSubmit";
import { removeCampaignNavigationSearchParams } from "@/canvas/features/FormV2/helpers/campaignUrls";
import { useShouldHideFundraise } from "@/canvas/features/FormV2/hooks/useShouldHideFundraise";
import { useTrackFormLogoClicked } from "@/canvas/features/FormV2/hooks/useTrackFormLogoClicked";
import { useUtmForm } from "@/canvas/features/FormV2/hooks/useUtmForm";
import { PeerToPeerDonateToPrimaryCampaignStep1Layout } from "@/canvas/features/FormV2/steps/step1/PeerToPeer/PeerToPeerDonateToPrimaryCampaignStep1Layout";
import { PeerToPeerPrimaryCampaignStep1Layout } from "@/canvas/features/FormV2/steps/step1/PeerToPeer/PeerToPeerPrimaryCampaignStep1Layout";
import { PeerToPeerV2CoreInfo } from "@/canvas/features/FormV2/steps/step1/shared/CoreInfo/PeerToPeerV2CoreInfo";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useQueryState, parseAsBoolean, parseAsStringLiteral } from "@/common/hooks/useQueryState";
import { useThemedFormZeffyLogoUrl } from "@/common/hooks/useThemedFormZeffyLogoUrl";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { ThemedContainerLoader } from "@/components/Loaders/ThemedContainerLoader";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useDebounce } from "@/components/TextField/useDebounce";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";

import { NewDonationPill } from "@/canvas/features/FormV2/blocks/donor-list/NewDonationPill/NewDonationPill";
import { StandaloneNewDonationPillSlot } from "@/canvas/features/FormV2/blocks/donor-list/NewDonationPill/StandaloneNewDonationPillSlot";
import { StickyNewDonationPill } from "@/canvas/features/FormV2/blocks/donor-list/NewDonationPill/StickyNewDonationPill";
import { useAnnouncedDonation } from "@/canvas/features/FormV2/blocks/donor-list/NewDonationPill/useAnnouncedDonation";
import { useNewDonationPill } from "@/canvas/features/FormV2/blocks/donor-list/NewDonationPill/useNewDonationPill";
import { NewDonationAnnouncementProvider } from "@/canvas/features/FormV2/contexts/newDonationAnnouncementContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { FormDivider } from "@/forms-design-system/FormDivider";

export const PeerToPeerPrimaryCampaignStep1: FC = () => {
  const { isoLocale } = useLocaleContext();
  const { isEmbedV2, displayedFormAmount } = useContext(FrontendFormContext);
  const { formObject, hasBanner, form, formCurrency, formCategory, bannerAspectRatio, isEmbed } = useFormV2Context(
    FormType.DonationForm
  );

  const shouldDisplayDonorList = formObject.displayDonorList;

  const {
    hasDonations,
    hasMoreDonations,
    latestDonations,
    isInitialLoading: isDonorsListFirstLoading,
  } = useDonorsListBlockState({
    donationFormId: formObject.id,
    enabled: shouldDisplayDonorList,
  });

  const { donation: donationToAnnounce, isUndecided: isDonationUndecided } = useAnnouncedDonation({
    formId: formObject.id,
    enabled: !formObject.isArchived,
    shouldDisplayDonorList,
    latestDonations,
  });

  const { isSmallScreen } = useMediaQuery();

  const announcement = useNewDonationPill({
    formId: formObject.id,
    announcementScopeId: formObject.campaignId ?? formObject.id,
    donation: donationToAnnounce,
    isDonationUndecided,
    canAnnounce: !shouldDisplayDonorList || latestDonations.length > 1,
    layout: { isSmallScreen, shouldDisplayDonorList },
  });
  const {
    donation: announcedDonation,
    isPillVisible,
    isTagVisible,
    isAnnouncedRowHeldBack,
    isAnnouncementUndecided,
    onPillAnimationEnd,
  } = announcement;

  const isAnnouncementSettling = isAnnouncementUndecided || (shouldDisplayDonorList && isDonorsListFirstLoading);

  const newDonationPill = announcedDonation ? (
    <NewDonationPill
      donorName={announcedDonation.fullName}
      amountInCents={announcedDonation.amountInCents}
      currency={announcedDonation.currency}
      isEcho={isSmallScreen}
      onAnimationEnd={onPillAnimationEnd}
    />
  ) : undefined;

  const isCampaign = !!formObject.target;
  const isOneColumnLayout = !hasBanner && !hasDonations && !isCampaign;
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const { isPreview, currentStepKey } = usePreviewContext();
  const { canSubmitFirstStep } = useDonationFirstStepSubmit();
  const utm = useUtmForm();
  const urlBuilder = useUrlBuilder();

  const onTrackLogoClick = useTrackFormLogoClicked({
    formType: FormType.DonationForm,
    formCategory,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const { data: getFundraisersSummaryResponse, isLoading } = trpc.form_getCampaignFundraisersSummary.useQuery({
    campaignId: formObject.campaignId!,
    locale: getLocalesFromIsoLocale(isoLocale),
  });

  const [donateQuery] = useQueryState("donate", parseAsBoolean.withDefault(false).withOptions({ history: "push" }));
  const [fundraisersQuery, setFundraisersQuery] = useQueryState(
    "fundraisers",
    parseAsStringLiteral(["view-all", "donate-to-campaign"] as const).withOptions({ history: "push" })
  );
  const [fundraiseQuery, setFundraiseQuery] = useQueryState(
    PeerToPeerQueryParams.OpenFundraiser,
    parseAsBoolean.withDefault(false).withOptions({ history: "push" })
  );

  const fundraisers = getFundraisersSummaryResponse ?? {
    hasTeamCampaigns: false,
    hasIndividualCampaigns: false,
    items: [],
    hasMore: false,
  };
  const shouldDisplayFundraisers = fundraisers.items.length > 0;

  const isDonateMode = getIsDonateMode({
    allowTeamCreation: Boolean(formObject.allowTeamCreation),
    allowFundraiserCreation: Boolean(formObject.allowFundraiserCreation),
    allowJoinTeam: Boolean(formObject.allowJoinTeam),
    hasFundraisers: fundraisers.items.length > 0,
    isPreview,
    liveFormEditorStep: currentStepKey,
    donateQuery,
  });

  const isFundraisersModalOpen = fundraisersQuery === "view-all" || fundraisersQuery === "donate-to-campaign";
  const isFundraiseDialogOpen = fundraiseQuery;

  const [searchText, setSearchTextState] = useState("");

  const { setDebouncedValue: setSearchText } = useDebounce({
    onChange: useCallback((value: string) => {
      setSearchTextState(value);
    }, []),
    debounced: 300,
  });

  const {
    open: donorsListDialogOpen,
    onOpen: onDonorsListDialogOpen,
    onClose: onDonorsListDialogClose,
  } = useDonorsListDialogState();

  const donorsDialogServerState = useDonorsListDialogServerState({
    donationFormId: formObject.id,
    open: donorsListDialogOpen,
  });

  const { isFetching: isSearchingFundraisers, data: searchFundraisersSummaryResponse } =
    trpc.form_getCampaignFundraisersSummary.useQuery(
      {
        campaignId: formObject.campaignId!,
        search: searchText,
        locale: getLocalesFromIsoLocale(isoLocale),
      },
      { enabled: !!searchText && !isFundraisersModalOpen }
    );

  const searchedFundraisers = searchFundraisersSummaryResponse ?? {
    items: [],
    hasMore: false,
  };

  const shouldHideFundraise = useShouldHideFundraise();

  const openPeerToPeerRegistrationForm = useOpenPeerToPeerRegistrationForm(formObject.ticketingId ?? undefined);

  const isSquareBanner = bannerAspectRatio === "1/1";
  const organizationIsGiftAidEligible = formObject.organization?.isGiftAidEligible;
  const donorCanOptInGiftAid = Boolean(organizationIsGiftAidEligible && displayedFormAmount);

  const renderFundraisersTile = () => {
    const fundraisersList = searchText ? searchedFundraisers.items : fundraisers.items;

    return (
      <FundraisersListBlock
        isLoading={isSearchingFundraisers}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        onViewAllClick={() => setFundraisersQuery("view-all")}
        showViewAllButton={fundraisers.hasMore}
      >
        {fundraisersList.map(({ type, path, ...fundraiser }, index) => {
          if (type === "team") {
            return (
              <TeamFundraiserCard
                key={index}
                {...fundraiser}
                currency={formCurrency}
                href={urlBuilder({
                  route: RouteBuilders.buildPeerToPeerV2CampaignLink({
                    type: "team",
                    isoLocale,
                    path,
                    isEmbedV2,
                  }),
                  searchParams: removeCampaignNavigationSearchParams,
                })}
              />
            );
          }

          return (
            <IndividualFundraiserCard
              key={index}
              {...fundraiser}
              currency={formCurrency}
              href={urlBuilder({
                route: RouteBuilders.buildPeerToPeerV2CampaignLink({
                  type: "individual",
                  isoLocale,
                  path,
                  isEmbedV2,
                }),
                searchParams: removeCampaignNavigationSearchParams,
              })}
              isPartOfTeam={fundraiser.isPartOfTeam}
            />
          );
        })}
      </FundraisersListBlock>
    );
  };

  const listedDonations = isAnnouncedRowHeldBack
    ? latestDonations.filter((donation) => donation.id !== announcedDonation?.id)
    : latestDonations;

  const donorsListComponent = hasDonations ? (
    <DonorsListBlock showViewAllButton={hasMoreDonations} onViewAllClick={onDonorsListDialogOpen}>
      {isPillVisible && newDonationPill}
      {listedDonations.map((donation, index) => (
        <DonorListItem
          {...donation}
          key={donation.id ?? index}
          isNew={isTagVisible && donation.id === announcedDonation?.id}
        />
      ))}
    </DonorsListBlock>
  ) : undefined;

  const standaloneNewDonationPill =
    !shouldDisplayDonorList && announcedDonation && !isSmallScreen ? (
      <StandaloneNewDonationPillSlot>{isPillVisible && newDonationPill}</StandaloneNewDonationPillSlot>
    ) : undefined;

  const donorsComponent = donorsListComponent ?? standaloneNewDonationPill;

  if (isLoading) {
    return <ThemedContainerLoader fullPage />;
  }

  return (
    <NewDonationAnnouncementProvider value={announcement} isSettling={isAnnouncementSettling}>
      <HeartsAnimationProvider>
        <BurstProvider type="heart" brandColorHex={formObject.color}>
          {isDonateMode ? (
            <PeerToPeerDonateToPrimaryCampaignStep1Layout
              isEmbed={isEmbed}
              contextDrawerHeight={isExpanded ? bannerHeight : 0}
              isSquareBanner={isSquareBanner}
              isOneColumn={isOneColumnLayout}
              slots={{
                banner: hasBanner ? (
                  <MobileCampaignBanner useSquareRatioOnMobile={Boolean(isSquareBanner)} />
                ) : undefined,
                summary: (
                  <PeerToPeerPrimaryCampaignSummary
                    coreInfo={<PeerToPeerV2CoreInfo campaignType={<PrimaryCampaignTypeChip />} />}
                  />
                ),
                coreInfo: <PeerToPeerV2CoreInfo campaignType={<PrimaryCampaignTypeChip />} />,
                donors: donorsComponent,
                donationAmounts: <Amounts />,
                fundDesignation: <FundDesignation isPreview={isPreview} />,
                giftAid: donorCanOptInGiftAid && (
                  <GiftAidBlock
                    amount={displayedFormAmount}
                    form={form as UseFormReturn<DonationPaymentInput | TicketingPaymentInput>}
                    organizationName={formObject.organization?.name || ""}
                  />
                ),
                generateReceipt: <GenerateReceiptInfobox />,
                zeffyBusinessModel: <ZeffyBusinessModelInfobox />,
                checkoutFooter: <DonationFormCheckoutFooter />,
                divider: !isOneColumnLayout ? <FormDivider sx={{ width: "100%" }} /> : undefined,
              }}
              showCheckoutFooter={canSubmitFirstStep && !isPreview}
              logoHref={logoHref}
              onLogoClick={onTrackLogoClick}
            />
          ) : (
            <PeerToPeerPrimaryCampaignStep1Layout
              isEmbed={isEmbed}
              contextDrawerHeight={isExpanded ? bannerHeight : 0}
              isSquareBanner={isSquareBanner}
              isOneColumn={isOneColumnLayout}
              slots={{
                banner: hasBanner ? (
                  <MobileCampaignBanner useSquareRatioOnMobile={Boolean(isSquareBanner)} />
                ) : undefined,
                summary: (
                  <PeerToPeerPrimaryCampaignSummary
                    coreInfo={<PeerToPeerV2CoreInfo campaignType={<PrimaryCampaignTypeChip />} />}
                  />
                ),
                coreInfo: <PeerToPeerV2CoreInfo campaignType={<PrimaryCampaignTypeChip />} />,
                fundraisers: shouldDisplayFundraisers ? renderFundraisersTile() : undefined,
                donors: donorsComponent,
                donateOrFundraiseOptions: (
                  <PeerToPeerPrimaryCampaignActions
                    allowTeamCreation={Boolean(formObject.allowTeamCreation)}
                    allowJoinTeam={Boolean(formObject.allowJoinTeam)}
                    allowFundraiserCreation={Boolean(formObject.allowFundraiserCreation)}
                    hasRegistration={!!formObject.ticketingId}
                    hasTeamCampaigns={fundraisers.hasTeamCampaigns}
                    hasIndividualCampaigns={fundraisers.hasIndividualCampaigns}
                    onDonateButtonClick={() => setFundraisersQuery("donate-to-campaign")}
                    onFundraiseButtonClick={() => setFundraiseQuery(true)}
                    onRegisterButtonClick={openPeerToPeerRegistrationForm}
                    shouldHideFundraise={shouldHideFundraise}
                  />
                ),
                divider: !isOneColumnLayout ? <FormDivider sx={{ width: "100%" }} /> : undefined,
              }}
              logoHref={logoHref}
              onLogoClick={onTrackLogoClick}
            />
          )}
          {hasDonations && (
            <DonorsListDialog
              {...donorsDialogServerState}
              open={donorsListDialogOpen}
              onClose={onDonorsListDialogClose}
            />
          )}
          <FundraisersInformationDialog
            mode={fundraisersQuery === "view-all" ? "find-fundraiser-campaign" : "donate-to-campaign"}
            campaignId={formObject.campaignId!}
            open={isFundraisersModalOpen}
            search={searchText}
            onSearchChange={setSearchText}
            formCurrency={formCurrency}
            onClose={() => setFundraisersQuery(null)}
          />
          <FundraiseDialog
            open={isFundraiseDialogOpen}
            onClose={() => setFundraiseQuery(false)}
            campaignId={formObject.campaignId as string}
            allowTeamCreation={Boolean(formObject.allowTeamCreation)}
            allowFundraiserCreation={Boolean(formObject.allowFundraiserCreation)}
            allowJoinTeam={Boolean(formObject.allowJoinTeam)}
          />
          {isSmallScreen && isPillVisible && announcedDonation && (
            <StickyNewDonationPill
              donorName={announcedDonation.fullName}
              amountInCents={announcedDonation.amountInCents}
              currency={announcedDonation.currency}
              onAnimationEnd={onPillAnimationEnd}
            />
          )}
        </BurstProvider>
      </HeartsAnimationProvider>
    </NewDonationAnnouncementProvider>
  );
};
